import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, Mail, KeyRound, Eye } from "lucide-react";
import { UserDetailsModal } from "./UserDetailsModal";
import { LastSeen } from "./LastSeen";
import { toast } from "sonner";

export function UsersTab({ users, reload }) {
  const [userDetails, setUserDetails] = useState(null);

  const openUserDetails = async (userId) => {
    setUserDetails({ loading: true, user_id: userId });
    try {
      const { data } = await api.get(`/admin/users/${userId}/details`);
      setUserDetails(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل تحميل بيانات المستخدم");
      setUserDetails(null);
    }
  };
  const closeUserDetails = () => setUserDetails(null);

  const resetUserPassword = async (u) => {
    if (u.auth_provider === "google") {
      toast.error("لا يمكن إعادة تعيين كلمة مرور حسابات Google");
      return;
    }
    const pwd = prompt(`كلمة المرور الجديدة للمستخدم ${u.name} (${u.email})؟\n(6 أحرف على الأقل)`);
    if (!pwd) return;
    if (pwd.length < 6) {
      toast.error("كلمة المرور قصيرة جدًا (6 أحرف على الأقل)");
      return;
    }
    try {
      await api.post(`/admin/users/${u.user_id}/reset-password`, { new_password: pwd });
      toast.success(`تم! كلمة المرور الجديدة: ${pwd}`, { duration: 10000 });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشلت العملية");
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-[#6B6B6B]">
          جميع المستخدمين المسجّلين. كلمات المرور مشفّرة ولا تُعرض لأسباب أمنية.
        </p>
      </div>
      <div className="jh-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] border-b border-[#EAEAEA] text-xs text-[#6B6B6B] uppercase tracking-wider">
              <tr>
                <th className="text-start px-4 py-3 font-semibold">المستخدم</th>
                <th className="text-start px-4 py-3 font-semibold">البريد الإلكتروني</th>
                <th className="text-start px-4 py-3 font-semibold">طريقة التسجيل</th>
                <th className="text-start px-4 py-3 font-semibold">الطلبات</th>
                <th className="text-start px-4 py-3 font-semibold">تاريخ الانضمام</th>
                <th className="text-start px-4 py-3 font-semibold">آخر ظهور</th>
                <th className="text-end px-4 py-3 font-semibold">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-10 text-[#6B6B6B]">لا يوجد مستخدمون</td></tr>
              ) : users.map((u) => (
                <tr key={u.user_id} className="border-b border-[#EAEAEA] last:border-0 hover:bg-[#F5F5F5]" data-testid={`admin-user-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-[#E05D3A] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                          {u.name?.[0]?.toUpperCase() || "U"}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold text-[#1A1A1A] truncate flex items-center gap-1.5">
                          {u.name}
                          {u.is_admin && <Shield className="w-3.5 h-3.5 text-[#E05D3A]" />}
                        </p>
                        <p className="text-xs text-[#6B6B6B] truncate">{u.user_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 text-[#1A1A1A]">
                      <Mail className="w-3.5 h-3.5 text-[#6B6B6B]" />
                      {u.email}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={u.auth_provider === "google" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"}>
                      {u.auth_provider === "google" ? "Google" : "إيميل/كلمة مرور"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-semibold">{u.order_count || 0}</span>
                    <span className="text-xs text-[#6B6B6B] ms-1">طلب</span>
                  </td>
                  <td className="px-4 py-3 text-[#6B6B6B] text-xs whitespace-nowrap">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("ar-SA") : "-"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" data-testid={`user-last-seen-${u.user_id}`}>
                    <LastSeen iso={u.last_seen_at} />
                  </td>
                  <td className="px-4 py-3 text-end">
                    <div className="flex items-center gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openUserDetails(u.user_id)}
                        className="rounded-full text-xs"
                        data-testid={`admin-view-user-${u.user_id}`}
                      >
                        <Eye className="w-3.5 h-3.5 me-1" />
                        التفاصيل
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resetUserPassword(u)}
                        disabled={u.auth_provider === "google"}
                        className="rounded-full text-xs"
                        data-testid={`admin-reset-pwd-${u.user_id}`}
                      >
                        <KeyRound className="w-3.5 h-3.5 me-1" />
                        إعادة تعيين
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <UserDetailsModal details={userDetails} onClose={closeUserDetails} />
    </div>
  );
}
